import { ResponsiveTable } from '../components/common/ResponsiveTable';
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { API_CONFIG, getApiUrl, getAuthHeaders } from '../config/api';
import type { Appointment } from '../components/calendar/types';
import { useThemedStyles } from '../theme/useThemedStyles';
import { formatDate } from '../utils/dateFormat';

interface Patient {
  id: number;
  firstName: string;
  secondName?: string;
  firstLastName: string;
  secondLastName?: string;
  email?: string;
  phone?: string;
  status: number;
}

interface Doctor {
  id: number;
  firstName: string;
  secondName?: string;
  lastName: string;
  secondLastName?: string;
  dateOfBirth: string;
  color?: string;
  createdAt: string;
  firstDayInClinic: string;
  patients?: Patient[];
  mainPatients?: Patient[];
}

interface Props {
  id: number;
  token: string;
  onBack: () => void;
  onSelectPatient: (id: number) => void;
}

const header: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24 };
const backBtn: React.CSSProperties = { background: 'transparent', border: 'none', fontSize: 24, cursor: 'pointer', padding: 0 };
const sectionTitle: React.CSSProperties = { fontSize: 18, fontWeight: 600, marginBottom: 16 };
const grid: React.CSSProperties = { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 16 };
const patientCard: React.CSSProperties = { borderRadius: 6, padding: 12, cursor: 'pointer', transition: 'background 0.2s' };
const infoRow: React.CSSProperties = { display: 'flex', gap: 24, marginBottom: 12 };
const label: React.CSSProperties = { fontSize: 14, width: 120 };
const value: React.CSSProperties = { fontSize: 14, fontWeight: 500 };

export default function DoctorProfile({ id, token, onBack, onSelectPatient }: Props) {
  const { t, i18n } = useTranslation();
  const theme = useThemedStyles();
  const [doctor, setDoctor] = useState<Doctor | null>(null);
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchDoctor = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(getApiUrl(`${API_CONFIG.ENDPOINTS.DOCTORS}/${id}`), {
        headers: getAuthHeaders(token)
      });
      if (!res.ok) throw new Error('Failed to load doctor details');
      const data = await res.json();
      setDoctor(data);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [id, token]);

  const fetchAppointments = useCallback(async () => {
    try {
      const res = await fetch(getApiUrl(API_CONFIG.ENDPOINTS.APPOINTMENTS), {
        headers: getAuthHeaders(token)
      });
      if (res.ok) {
        const data = await res.json();
        setAppointments(data);
      }
    } catch (e) {
      console.error(e);
    }
  }, [token]);

  useEffect(() => {
    fetchDoctor();
    fetchAppointments();
  }, [fetchDoctor, fetchAppointments]);

  const currentMonthAppointments = useMemo(() => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);
    
    return appointments.filter(a => {
      if (a.doctorId !== id && a.secondDoctorId !== id) return false;
      if (!a.startTime) return false;
      const d = new Date(a.startTime);
      return d >= start && d <= end;
    }).sort((a, b) => new Date(a.startTime!).getTime() - new Date(b.startTime!).getTime());
  }, [appointments, id]);

  const themedContainer: React.CSSProperties = { padding: 24, color: theme.text };
  const themedCard: React.CSSProperties = { ...theme.card, padding: 24, marginBottom: 24 };
  const themedSectionTitle: React.CSSProperties = { ...sectionTitle, color: theme.text };
  const themedLabel: React.CSSProperties = { ...label, color: theme.muted };
  const themedValue: React.CSSProperties = { ...value, color: theme.text };
  const themedPatientCard: React.CSSProperties = { ...patientCard, background: theme.inputBg, border: `1px solid ${theme.border}`, color: theme.text };

  if (loading) return <div style={themedContainer}>{t('common.loading', 'Loading...')}</div>;
  if (error) return <div style={themedContainer}>{t('common.error', 'Error')}: {error}</div>;
  if (!doctor) return null;

  const fullName = [doctor.firstName, doctor.secondName, doctor.lastName, doctor.secondLastName].filter(Boolean).join(' ');

  // Combine patients from both lists (Many-to-Many and MainDoctor) and deduplicate by ID
  const allPatients = Array.from(new Map(
    [...(doctor.patients || []), ...(doctor.mainPatients || [])].map(p => [p.id, p])
  ).values());

  // Filter: Show only New (0) and Active (1) patients
  const visiblePatients = allPatients.filter(p => p.status === 0 || p.status === 1);

  return (
    <div style={themedContainer}>
      <div className="responsive-row" style={header}>
        <button onClick={onBack} style={{ ...backBtn, color: theme.muted }}>←</button>
        <h2 style={{ margin: 0 }}>{fullName}</h2>
      </div>

      <div style={themedCard}>
        <h3 style={themedSectionTitle}>{t('doctors.profileInfo', 'Doctor Information')}</h3>
        <div className="responsive-row" style={infoRow}>
          <span style={themedLabel}>{t('doctors.firstName', 'First Name')}</span>
          <span style={themedValue}>{doctor.firstName}</span>
        </div>
        <div className="responsive-row" style={infoRow}>
          <span style={themedLabel}>{t('doctors.lastName', 'Last Name')}</span>
          <span style={themedValue}>{doctor.lastName}</span>
        </div>
        <div className="responsive-row" style={infoRow}>
          <span style={themedLabel}>{t('doctors.dob', 'Date of Birth')}</span>
          <span style={themedValue}>{formatDate(doctor.dateOfBirth)}</span>
        </div>
        <div className="responsive-row" style={infoRow}>
          <span style={themedLabel}>{t('doctors.firstDay', 'First Day in Clinic')}</span>
          <span style={themedValue}>{formatDate(doctor.firstDayInClinic)}</span>
        </div>
        <div className="responsive-row" style={infoRow}>
          <span style={themedLabel}>{t('doctors.color', 'Color')}</span>
          <div style={{ width: 24, height: 24, borderRadius: 4, background: doctor.color || '#667eea' }} />
        </div>
      </div>

      <div style={themedCard}>
        <h3 style={themedSectionTitle}>{t('doctors.thisMonthAppointments')} ({currentMonthAppointments.length})</h3>
        {currentMonthAppointments.length > 0 ? (
          <div className="responsive-table-scroll" style={{ overflowX: 'auto' }}>
            <ResponsiveTable><table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${theme.border}`, textAlign: 'left' }}>
                  <th style={{ padding: '8px 12px', color: theme.muted }}>{t('common.date')}</th>
                  <th style={{ padding: '8px 12px', color: theme.muted }}>{t('common.time')}</th>
                  <th style={{ padding: '8px 12px', color: theme.muted }}>{t('common.patient')}</th>
                  <th style={{ padding: '8px 12px', color: theme.muted }}>ID</th>
                  <th style={{ padding: '8px 12px', color: theme.muted }}>{t('common.treatment')}</th>
                </tr>
              </thead>
              <tbody>
                {currentMonthAppointments.map(app => {
                  const date = new Date(app.startTime!);
                  const patient = visiblePatients.find(p => p.id === app.patientId);
                  const patientName = patient 
                    ? [patient.firstName, patient.firstLastName].join(' ')
                    : (app.patient?.name || app.prospectName || 'Unknown');
                  
                  return (
                    <tr key={app.id} style={{ borderBottom: `1px solid ${theme.border}` }}>
                      <td style={{ padding: '8px 12px', color: theme.text }}>{formatDate(date)}</td>
                      <td style={{ padding: '8px 12px', color: theme.text }}>{date.toLocaleTimeString(i18n.language, { hour: '2-digit', minute: '2-digit' })}</td>
                      <td 
                        style={{ 
                          padding: '8px 12px', 
                          color: app.patientId ? theme.primary : theme.text, 
                          cursor: app.patientId ? 'pointer' : 'default' 
                        }} 
                        onClick={() => app.patientId && onSelectPatient(app.patientId)}
                      >
                        {patientName}
                      </td>
                      <td style={{ padding: '8px 12px', color: theme.muted }}>{app.patientId || '-'}</td>
                      <td style={{ padding: '8px 12px', color: theme.text }}>{app.treatment?.name || '-'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table></ResponsiveTable>
          </div>
        ) : (
          <div style={{ color: theme.muted, fontStyle: 'italic' }}>{t('doctors.noAppointments', 'No appointments this month')}</div>
        )}
      </div>

      <div>
        <h3 style={themedSectionTitle}>{t('doctors.assignedPatients', 'Assigned Patients')} ({visiblePatients.length})</h3>
        {visiblePatients.length > 0 ? (
          <div className="responsive-grid" style={grid}>
            {visiblePatients.map(p => {
              const pName = [p.firstName, p.secondName, p.firstLastName, p.secondLastName].filter(Boolean).join(' ');
              return (
                <div 
                  key={p.id} 
                  style={themedPatientCard}
                  onClick={() => onSelectPatient(p.id)}
                  onMouseEnter={(e) => e.currentTarget.style.background = theme.surface}
                  onMouseLeave={(e) => e.currentTarget.style.background = theme.inputBg}
                >
                  <div style={{ fontWeight: 500, marginBottom: 4 }}>{pName}</div>
                  {p.email && <div style={{ fontSize: 12, color: theme.muted }}>{p.email}</div>}
                  {p.phone && <div style={{ fontSize: 12, color: theme.muted }}>{p.phone}</div>}
                </div>
              );
            })}
          </div>
        ) : (
          <div style={{ color: theme.muted, fontStyle: 'italic' }}>{t('doctors.noPatients', 'No patients assigned')}</div>
        )}
      </div>
    </div>
  );
}


